import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Checkbox } from "@mui/material";
import InfoBox from "../info-box/InfoBox";
import { Flags, isSegFeatureEnabled, SegFlags, translate } from "../../../../../common/providers";
import { CustomLink, CustomTooltip, icon } from "../../../../../common/components";
import styles from "./ProjectSetup.module.css";
import { checkValidity, NavUtils } from "../../../../../navigation";

const ProjectSetup = ({ className }) => {
	const projectId = useSelector(({ context }) => context.project?.id);
	const setupSteps = useMemo(
		() => [
			{
				key: "DOCUMENTS",
				title: translate("dashboards.project.project-setup.documents.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.documents.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToDocumentCenter(projectId)}
							variant="text"
						>
							{translate("navigation:home.documents")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.documents.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.DOCUMENT_CENTER_READING],
				flag: [Flags.DOCUMENT_CENTER],
			},
			{
				key: "IMPLICIT_REQ",
				title: translate("dashboards.project.project-setup.implicit-req.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.implicit-req.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToProjectRequirements(projectId)}
							variant="text"
						>
							{translate("navigation:project.project-requirements")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.implicit-req.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.AI_IMPLICIT_REQUIREMENT_DETECTOR, SegFlags.PROJECT_REQUIREMENTS],
				flag: [Flags.REQUIREMENT_MATRIX],
			},
			{
				key: "IMPLICIT_REQ2",
				title: translate("dashboards.project.project-setup.my-review.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.my-review.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToProjectRequirements(projectId)}
							variant="text"
						>
							{translate("navigation:project.project-requirements")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate(
								(isSegFeatureEnabled(SegFlags.PROJECT_REQUIREMENTS) &&
									"dashboards.project.project-setup.my-review-req.description.pt2") ||
									"dashboards.project.project-setup.my-review.description.pt2"
							)}
						</span>
					</span>
				),
				flag: [Flags.REQUIREMENT_MATRIX],
				segFlag: [SegFlags.PROJECT_REQUIREMENTS],
			},
			{
				key: "CRITERIA_DETECTION",
				title: translate("dashboards.project.project-setup.criteria-detection.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.criteria-detection.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToProjectSheet(projectId)}
							variant="text"
						>
							{translate("navigation:project.criteria-detection")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.criteria-detection.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.AI_CRITERIA_DETECTION],
			},
			{
				key: "TEAM",
				title: translate("dashboards.project.project-setup.team.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.team.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToTeam(projectId)}
							variant="text"
						>
							{translate("navigation:home.team")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.team.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.TEAM],
				flag: [Flags.PARTNERS],
			},
			{
				key: "RIGTS_ON_DOCUMENT",
				title: translate("dashboards.project.project-setup.rights-on-documents.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.rights-on-documents.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToRightsOnDocuments(projectId)}
							variant="text"
						>
							{translate("navigation:home.rights-on-documents")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.rights-on-documents.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.RIGHTS_ON_DOCUMENTS, SegFlags.TEAM],
			},
			{
				key: "CATEGORIES",
				title: translate("dashboards.project.project-setup.categories.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.categories.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToProjectCategories(projectId)}
							variant="text"
						>
							{translate("common:categories")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.categories.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.CATEGORY],
				flag: [Flags.THEMATIC],
			},
			{
				key: "AICATEGORIES",
				title: translate("dashboards.project.project-setup.ai-categories.title"),
				description: (
					<span>
						<span className={styles.stepDescription__part1}>
							{translate("dashboards.project.project-setup.ai-categories.description.pt1")}
						</span>
						<CustomLink
							underlined
							className={styles.stepDescription__link}
							to={NavUtils.goToProjectCategories(projectId)}
							variant="text"
						>
							{translate("common:categories")}
						</CustomLink>
						<span className={styles.stepDescription__part2}>
							{translate("dashboards.project.project-setup.ai-categories.description.pt2")}
						</span>
					</span>
				),
				segFlag: [SegFlags.CATEGORY, SegFlags.AI_CONTENT_CATEGORIZER],
				flag: [Flags.THEMATIC],
			},
		],
		[projectId]
	);
	return (
		<InfoBox
			className={className}
			icon={icon.faClipboardCheck}
			title={translate("dashboards.project.project-setup.title")}
		>
			<div className={styles.ProjectSetup}>
				{setupSteps
					.filter((step) => checkValidity(step))
					.map((step) => (
						<div key={step.key} className={styles.step__container}>
							<CustomTooltip title={translate("dashboards.project.project-setup.hovering")}>
								<span>
									<Checkbox disabled className={styles.step__checkbox} size="small" />
								</span>
							</CustomTooltip>
							<div>
								<div className={styles.step__title}>{step.title}</div>
								<div className={styles.step__description}>{step.description}</div>
							</div>
						</div>
					))}
			</div>
		</InfoBox>
	);
};

export default ProjectSetup;
